import React from 'react';
import { Card } from 'antd';

const StatCard = ({
  title,
  value,
  suffix,
  icon,
  gradientFrom = 'from-rose-100',
  gradientTo = 'to-rose-50',
  isMobile = false,
  trendPercent,
  trendDirection,
  sparklineData = [],
  compact = false,
}) => {
  const hasTrend = typeof trendPercent === 'number' && trendDirection;

  const Sparkline = () => {
    if (!sparklineData || sparklineData.length === 0) return null;
    const width = 120;
    const height = 36;
    const max = Math.max(...sparklineData);
    const min = Math.min(...sparklineData);
    const range = max - min || 1;
    const step = width / (sparklineData.length - 1);
    const points = sparklineData
      .map((v, i) => {
        const x = i * step;
        const y = height - ((v - min) / range) * height;
        return `${x},${y}`;
      })
      .join(' ');
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="opacity-80">
        <polyline fill="none" stroke="#fb7185" strokeWidth="2" points={points} />
      </svg>
    );
  };

  if (compact) {
    return (
      <Card
        bordered={false}
        className={
          'shadow-md hover:shadow-lg transition-all duration-300 h-full rounded-xl ' +
          'bg-gradient-to-br border border-white/60 backdrop-blur ' +
          `${gradientFrom} ${gradientTo}`
        }
        bodyStyle={{ padding: isMobile ? 8 : 12 }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className={
                'inline-flex items-center justify-center rounded-lg bg-white/70 text-rose-500 shadow-sm ' +
                (isMobile ? 'w-6 h-6 text-base' : 'w-8 h-8 text-xl')
              }
            >
              {icon}
            </div>
            <span className={(isMobile ? 'text-xs' : 'text-sm') + ' text-gray-600 font-medium'}>
              {title}
            </span>
          </div>
          <div className="flex items-end gap-1">
            <span className={(isMobile ? 'text-base' : 'text-xl') + ' font-semibold text-gray-900'}>
              {value}
            </span>
            {suffix ? (
              <span className={(isMobile ? 'text-xs' : 'text-sm') + ' text-gray-500'}>{suffix}</span>
            ) : null}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className={
        'shadow-lg hover:shadow-xl transition-all duration-300 h-full rounded-2xl ' +
        'bg-gradient-to-br border border-white/60 backdrop-blur ' +
        `${gradientFrom} ${gradientTo}`
      }
      bodyStyle={{ padding: isMobile ? 12 : 18 }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div
            className={
              'inline-flex items-center justify-center rounded-xl bg-white/70 text-rose-500 shadow-sm ' +
              (isMobile ? 'w-7 h-7 text-lg' : 'w-9 h-9 text-2xl')
            }
          >
            {icon}
          </div>
          <span className={(isMobile ? 'text-xs' : 'text-sm') + ' text-gray-600 font-medium'}>
            {title}
          </span>
        </div>

        {hasTrend ? (
          <span
            className={
              'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ' +
              (trendDirection === 'up'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700')
            }
          >
            {trendDirection === 'up' ? '▲' : '▼'} {trendPercent}%
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex items-end gap-1">
        <span
          className={
            (isMobile ? 'text-xl' : 'text-3xl') +
            ' font-semibold tracking-tight text-gray-900'
          }
        >
          {value}
        </span>
        {suffix ? (
          <span className={(isMobile ? 'text-xs' : 'text-sm') + ' text-gray-500 ml-1'}>
            {suffix}
          </span>
        ) : null}
      </div>

      {sparklineData && sparklineData.length > 0 ? (
        <div className="mt-2">
          <Sparkline />
        </div>
      ) : null}
    </Card>
  );
};

export default StatCard;


